import { RedeemMerchantGiftCardWithOtpMutation, useRedeemMerchantGiftCardWithOtpMutation } from '@/__generated__/types';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { PATH_DASHBOARD } from '@/routes/paths';
import { fCurrency } from '@/utils/formatNumber';
import { fDateTimeTimestamp } from '@/utils/formatTime';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoadingButton from '@mui/material/Button';
import { Box, Card, CardContent, Divider, InputAdornment, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MerchantGiftCardContactField from './components/MerchantGiftCardContactField';
import useMerchantGiftCardOtp from './hooks/useMerchantGiftCardOtp';

interface MerchantGiftCardRedeemProps {
    merchantId?: string;
}

type RedeemedMerchantGiftCard = NonNullable<RedeemMerchantGiftCardWithOtpMutation['redeemMerchantGiftCardWithOtp']>;

const MerchantGiftCardRedeem = ({ merchantId }: MerchantGiftCardRedeemProps): JSX.Element => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbarHelper();

    const [inputRedeemCode, setInputRedeemCode] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [redeemedGiftCard, setRedeemedGiftCard] = useState<RedeemedMerchantGiftCard | null>(null);

    const {
        contactMethod,
        setContactMethod,
        email,
        setEmail,
        phone,
        setPhone,
        phoneCountry,
        setPhoneCountry,
        identifier,
        verificationCode,
        setVerificationCode,
        otpId,
        isCodeSent,
        isSendingCode,
        resendCountdown,
        sendOtp
    } = useMerchantGiftCardOtp({ merchantId, requireRedeemCode: true });

    const [redeemMerchantGiftCardWithOtpMutation] = useRedeemMerchantGiftCardWithOtpMutation();

    const formatCurrency = (amountInCents?: number | null): string => fCurrency((amountInCents || 0) / 100);
    const canNavigateToBalance = Boolean(merchantId);

    const handleSendVerificationCode = async (): Promise<void> => {
        const isSuccess = await sendOtp({ redeemCode: inputRedeemCode });
        if (isSuccess) {
            setIsVerified(false);
            setRedeemedGiftCard(null);
        }
    };

    const handleVerifyCode = async (): Promise<void> => {
        const otp = verificationCode.trim();
        const redeemCodeValue = inputRedeemCode.trim();

        if (!otp) {
            showSnackbar(t('giftCard.pleaseEnterCode'), 'error');
            return;
        }
        if (!redeemCodeValue) {
            showSnackbar(t('giftCard.pleaseEnterRedeemCode'), 'error');
            return;
        }
        if (!merchantId || !otpId) {
            showSnackbar(t('giftCard.invalidCode'), 'error');
            return;
        }

        setIsVerifying(true);
        try {
            const { data: redeemData } = await redeemMerchantGiftCardWithOtpMutation({
                variables: {
                    redeemCode: redeemCodeValue,
                    otpId,
                    otp,
                    merchantId
                }
            });

            if (!redeemData?.redeemMerchantGiftCardWithOtp) {
                showSnackbar(t('giftCard.loadError'), 'error');
                return;
            }

            setIsVerified(true);
            setRedeemedGiftCard(redeemData.redeemMerchantGiftCardWithOtp);
            showSnackbar(t('giftCard.verificationSuccessful'), 'success');
        } catch {
            showSnackbar(t('giftCard.invalidCode'), 'error');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleGoToBalance = (): void => {
        if (!merchantId) return;
        navigate(PATH_DASHBOARD.public.merchantGiftCardBalance(merchantId));
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 560, px: { xs: 2, sm: 0 } }}>
            <Card
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'common.white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: theme.customShadows.primary
                }}
            >
                <CardGiftcardIcon
                    sx={{ position: 'absolute', top: -24, right: -24, fontSize: 160, opacity: 0.12, color: 'common.white' }}
                />
                <CardContent sx={{ position: 'relative', zIndex: 1, py: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                bgcolor: alpha(theme.palette.common.white, 0.2),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CardGiftcardIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {t('giftCard.title')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                {t('giftCard.subtitle')}
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {!isVerified && (
                <Card sx={{ mt: 3, boxShadow: theme.customShadows.z8, borderRadius: 2 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t('giftCard.verifyYourIdentity')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('giftCard.verifyIdentityDescription')}
                                </Typography>
                            </Box>

                            <Stack spacing={2.5}>
                                <TextField
                                    fullWidth
                                    label={t('giftCard.redeemCode')}
                                    value={inputRedeemCode}
                                    onChange={(e) => setInputRedeemCode(e.target.value)}
                                    placeholder={t('giftCard.redeemCodePlaceholder')}
                                    disabled={isCodeSent}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CardGiftcardIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />

                                <MerchantGiftCardContactField
                                    contactMethod={contactMethod}
                                    onContactMethodChange={setContactMethod}
                                    email={email}
                                    onEmailChange={setEmail}
                                    phone={phone}
                                    onPhoneChange={setPhone}
                                    phoneCountry={phoneCountry}
                                    onPhoneCountryChange={setPhoneCountry}
                                    disabled={isCodeSent}
                                />

                                {!isCodeSent && (
                                    <LoadingButton
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        loading={isSendingCode}
                                        onClick={handleSendVerificationCode}
                                        disabled={resendCountdown > 0}
                                        sx={{ height: 48, fontSize: '1rem', fontWeight: 600 }}
                                    >
                                        {resendCountdown > 0
                                            ? t('giftCard.resendIn', { seconds: resendCountdown })
                                            : t('giftCard.sendCode')}
                                    </LoadingButton>
                                )}
                            </Stack>

                            {isCodeSent && (
                                <Box sx={{ mt: 1, pt: 3, borderTop: `1px dashed ${theme.palette.divider}` }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                            {t('giftCard.verificationCodeSentTo', 'A code has been sent to')} <b>{identifier}</b>
                                        </Typography>
                                        <LoadingButton
                                            variant="outlined"
                                            size="small"
                                            loading={isSendingCode}
                                            onClick={handleSendVerificationCode}
                                            disabled={resendCountdown > 0}
                                            sx={{ minWidth: 120 }}
                                        >
                                            {resendCountdown > 0
                                                ? t('giftCard.resendIn', { seconds: resendCountdown })
                                                : t('giftCard.resendCode')}
                                        </LoadingButton>
                                    </Stack>

                                    <TextField
                                        fullWidth
                                        label={t('giftCard.verificationCode')}
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        placeholder={t('giftCard.enterCode')}
                                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                                    />
                                    <LoadingButton
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        loading={isVerifying}
                                        onClick={handleVerifyCode}
                                        sx={{ mt: 3, height: 48, fontSize: '1rem', fontWeight: 600 }}
                                    >
                                        {t('giftCard.verifyAndRedeem', 'Verify & Redeem')}
                                    </LoadingButton>
                                </Box>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {isVerified && redeemedGiftCard && (
                <Card sx={{ mt: 2, boxShadow: theme.customShadows.z8, borderRadius: 2 }}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                        <Stack alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.success.main, 0.1)
                                }}
                            >
                                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
                                {redeemedGiftCard?.message || t('giftCard.verificationSuccessful')}
                            </Typography>
                        </Stack>

                        <Box sx={{ bgcolor: theme.palette.background.neutral, borderRadius: 1.5, p: 2 }}>
                            <Stack spacing={1.5}>
                                <Typography variant="overline" sx={{ color: 'text.disabled', lineHeight: 1 }}>
                                    {t('giftCard.redeemDetails')}
                                </Typography>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.amount')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                                        {formatCurrency(redeemedGiftCard.amount)}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.remainingBalance')}
                                    </Typography>
                                    <Typography variant="subtitle2" color="primary.main" sx={{ fontSize: '0.95rem' }}>
                                        {formatCurrency(redeemedGiftCard.balance)}
                                    </Typography>
                                </Stack>

                                <Divider sx={{ borderStyle: 'dashed', my: 0.5 }} />

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.redeemCodeLabel')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                        {redeemedGiftCard.redeemCode}
                                    </Typography>
                                </Stack>

                                <Divider sx={{ borderStyle: 'dashed', my: 0.5 }} />

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.recipient')}
                                    </Typography>
                                    <Typography variant="body2">{redeemedGiftCard.recipientName}</Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.email')}
                                    </Typography>
                                    <Typography variant="body2">{redeemedGiftCard.recipientEmail}</Typography>
                                </Stack>

                                {redeemedGiftCard.recipientPhone && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {t('giftCard.phone')}
                                        </Typography>
                                        <Typography variant="body2">{redeemedGiftCard.recipientPhone}</Typography>
                                    </Stack>
                                )}

                                {redeemedGiftCard.sender && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {t('giftCard.sender')}
                                        </Typography>
                                        <Typography variant="body2">{redeemedGiftCard.sender}</Typography>
                                    </Stack>
                                )}

                                {redeemedGiftCard.description && (
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                                            {t('giftCard.description')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'right' }}>
                                            {redeemedGiftCard.description}
                                        </Typography>
                                    </Stack>
                                )}

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('giftCard.updatedAt')}
                                    </Typography>
                                    <Typography variant="body2">{fDateTimeTimestamp(redeemedGiftCard.updatedAt)}</Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        <LoadingButton
                            fullWidth
                            size="large"
                            variant="outlined"
                            onClick={handleGoToBalance}
                            disabled={!canNavigateToBalance}
                            sx={{ mt: 3 }}
                        >
                            {t('giftCard.checkBalance', 'Check Balance')}
                        </LoadingButton>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default MerchantGiftCardRedeem;
